import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Send,
  Mail,
  AlertCircle,
  ChevronRight,
  Layout,
  Users,
  UserCircle2,
  FileText,
  Sparkles,
  Trash2,
  PlusCircle,
  MessageSquare,
  Database,
  Target,
  Info
} from "lucide-react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from "../ui/select";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { cn } from "../ui/utils";
import API from "../api/api01";
import { Card, CardContent } from "../ui/card";
import { Separator } from "../ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
}

interface CommunicationTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  is_custom: boolean;
  type: "email" | "whatsapp" | "message";
}

interface BulkCommunicationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContacts: Contact[];
  initialType?: string;
}

export function BulkCommunicationPanel({
  isOpen,
  onClose,
  selectedContacts,
  initialType,
}: BulkCommunicationPanelProps) {
  const [campaignName, setCampaignName] = useState("");
  const [campaignType, setCampaignType] = useState(initialType || "");
  const [fromEmail, setFromEmail] = useState("info@xeroalpha.com");
  const [fromName, setFromName] = useState("XeroAlpha");
  const [subject, setSubject] = useState("");
  const [sendTo, setSendTo] = useState<string>("")
  const [emailTemplate, setEmailTemplate] = useState("direct-login");
  const [emailBody, setEmailBody] = useState("");
  const [ctaText, setCtaText] = useState("Complete your Application");
  const [ctaLink, setCtaLink] = useState("this link");
  const [availableCredits] = useState(26644633);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([
    { id: "direct-login", name: "Direct Login Link Email", subject: "Direct Login Link | XeroAlpha", body: emailBody, is_custom: false, type: "email" },
    { id: "welcome", name: "Welcome Email", subject: "Welcome to XeroAlpha", body: "Welcome to XeroAlpha...", is_custom: false, type: "email" },
    { id: "follow-up", name: "Follow-up Email", subject: "Follow-up Email", body: "Just checking in...", is_custom: false, type: "email" },
    { id: "reminder", name: "Application Reminder", subject: "Reminder Email", body: "Reminder to complete your application...", is_custom: false, type: "email" },

    // WhatsApp Templates
    { id: "whatsapp-welcome", name: "WhatsApp Welcome", subject: "", body: "Hi {{ud.name}}, welcome to XeroAlpha! We are excited to have you on board.", is_custom: false, type: "whatsapp" },
    { id: "whatsapp-reminder", name: "WhatsApp Application Reminder", subject: "", body: "Hi {{ud.name}}, this is a reminder to complete your application at XeroAlpha.", is_custom: false, type: "whatsapp" },

    // SMS Templates
    { id: "sms-general", name: "General SMS", subject: "", body: "Hi {{ud.name}}, update from XeroAlpha: Your application is being processed.", is_custom: false, type: "message" },
    { id: "sms-otp", name: "OTP Verification", subject: "", body: "Your XeroAlpha verification code is {{otp}}.", is_custom: false, type: "message" }
  ]);

  const [isAddCustomOpen, setIsAddCustomOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateSubject, setNewTemplateSubject] = useState("");
  const [newTemplateBody, setNewTemplateBody] = useState("");

  const handleDeleteTemplate = (id: string) => {
    const filtered = templates.filter(t => t.id !== id);
    setTemplates(filtered);
    if (emailTemplate === id) {
      setEmailTemplate("direct-login");
    }
    toast.success("Custom template deleted");
  };

  useEffect(() => {
    if (initialType) {
      setCampaignType(initialType);
    }
  }, [initialType]);

  // Lock body & main scroll when panel is open
  useEffect(() => {
    const mainArea = document.querySelector("main");
    if (isOpen) {
      document.body.style.setProperty("overflow", "hidden", "important");
      document.documentElement.style.setProperty("overflow", "hidden", "important");
      if (mainArea) mainArea.style.setProperty("overflow", "hidden", "important");
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (mainArea) mainArea.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (mainArea) mainArea.style.overflow = "";
    };
  }, [isOpen]);

  const waNumber = "+91 98765 43210";
  const waName = "XeroAlpha Official";
  const smsSenderId = "XA-PRIME";
  const smsName = "XeroAlpha Corp";

  const getAuthorityDetails = () => {
    if (campaignType === "whatsapp") {
      return {
        label1: "WhatsApp Business Account",
        value1: waNumber,
        label2: "Verified Business Identity",
        value2: waName,
      };
    } else if (campaignType === "message") {
      return {
        label1: "SMS Gateway Authority",
        value1: smsSenderId,
        label2: "Sender Authority (6-char ID)",
        value2: smsName,
      };
    } else {
      return {
        label1: "Mail Server Authority",
        value1: fromEmail,
        label2: "Authenticated Name",
        value2: fromName,
      };
    }
  };

  const authDetails = getAuthorityDetails();

  useEffect(() => {
    if (emailTemplate === "direct-login") {
      setEmailBody(`Hi {{ud.name|Registered Name}},\n\nThis is *{{loa.counsellor}}* your senior academic counselor from XeroAlpha. You can start filling up your application form through ${ctaLink}.\n\n`);
      setSubject("Direct Login Link | XeroAlpha");
    } else {
      const selected = templates.find(t => t.id === emailTemplate);
      if (selected) {
        setSubject(selected.subject);
        setEmailBody(selected.body);
      }
    }
  }, [emailTemplate, ctaLink, templates]);

  useEffect(() => {
    const firstRelevantTemplate = templates.find(t => t.type === (campaignType === "whatsapp" || campaignType === "message" ? campaignType : "email"));
    if (firstRelevantTemplate && !templates.find(t => t.id === emailTemplate && t.type === (campaignType === "whatsapp" || campaignType === "message" ? campaignType : "email"))) {
      setEmailTemplate(firstRelevantTemplate.id);
    }
  }, [campaignType, templates, emailTemplate]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!campaignName.trim()) newErrors.campaignName = "Campaign name is required";
    if (!campaignType) newErrors.campaignType = "Campaign type is required";
    if (campaignType === "email" && !subject.trim()) newErrors.subject = "Subject is required";
    if (!emailBody.trim()) newErrors.emailBody = "Message content is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [isSending, setIsSending] = useState(false);

  const handleSendNow = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (selectedContacts.length === 0) {
      toast.error("No contacts selected");
      return;
    }

    setIsSending(true);

    try {
      const payload = {
        campaign_name: campaignName,
        campaign_type: campaignType || "promotional",
        from_email: fromEmail,
        from_name: fromName,
        send_to: sendTo || "all",
        subject: subject,
        body: emailBody,
        template_id: null,
        contact_ids: selectedContacts.map(c => c.id)
      };

      const response = await API.post("/campaigns/send", payload);

      if (response.status !== 200) {
        throw new Error("Failed to send campaign");
      }

      toast.success(`Bulk email dispatched to ${selectedContacts.length} contact(s) successfully!`);
      handleClose();
    } catch (err: any) {
      console.error("Bulk Send Error Full Info:", err);
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: any) => `${d.loc.join('.')}: ${d.msg}`).join(', ')
          : err.message || "Something went wrong sending emails.";
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setCampaignName("");
    setCampaignType("");
    setSubject("");
    setEmailBody("");
    setErrors({});
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm shadow-inner"
            onClick={handleClose}
          />

          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={cn(
              "fixed inset-y-0 right-0 z-[100] grid grid-rows-[auto_auto_1fr_auto] h-screen bg-slate-50 dark:bg-[#020617] shadow-2xl border-l border-slate-200 dark:border-slate-800 overflow-hidden",
              "w-full"
            )}
          >
            {/* Header Section */}
            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-[#020617] shrink-0">

              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white leading-tight">Bulk Communication</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Create and dispatch multi-channel campaigns</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <Target className="h-4 w-4 text-blue-500" />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    {selectedContacts.length} Recipients Selected
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="rounded-full hover:bg-red-500/10 hover:text-red-500 transition-all"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Quick Stats / Info Bar */}
            <div className="px-6 py-3 bg-slate-50 dark:bg-[#020617] border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-4 shrink-0">
              <div className="flex items-center gap-3 bg-blue-500/5 dark:bg-blue-500/10 px-3 py-1.5 rounded-full border border-blue-500/10">
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                  {selectedContacts.length} Recipients Selected
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-slate-400" />
                  <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Credits:</span>
                  <Badge variant="secondary" className="font-mono bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                    {availableCredits.toLocaleString()}
                  </Badge>
                </div>
              </div>
            </div>

            <ScrollArea className="h-full min-h-0">
              <div className="p-6 max-w-5xl mx-auto space-y-8 pb-12">
                {/* Form Content */}
                <div className="space-y-8 pb-4">

                  {/* Section: Campaign Setup */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 text-slate-900 dark:text-white mb-2">
                      <Layout className="h-5 w-5 text-blue-500" />
                      <h3 className="font-bold text-lg">Campaign Strategy</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ring-1 ring-slate-100 dark:ring-slate-800 p-6 rounded-2xl bg-white dark:bg-[#0f172a]">
                      <div className="space-y-2">
                        <Label htmlFor="campaign-name" className="text-sm font-semibold flex items-center gap-1">
                          Campaign Identity <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="campaign-name"
                          placeholder="e.g., Spring 2026 Welcome"
                          value={campaignName}
                          onChange={(e) => {
                            setCampaignName(e.target.value);
                            setErrors({ ...errors, campaignName: "" });
                          }}
                          className={cn(
                            "h-11 rounded-xl bg-white dark:bg-[#020617] border-slate-200 dark:border-slate-800",
                            errors.campaignName && "border-red-500 focus-visible:ring-red-500"
                          )}
                        />
                        {errors.campaignName && <p className="text-xs text-red-500 font-medium">{errors.campaignName}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="campaign-type" className="text-sm font-semibold flex items-center gap-1">
                          Campaign Magnitude <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={campaignType}
                          onValueChange={(value) => {
                            setCampaignType(value);
                            setErrors({ ...errors, campaignType: "" });
                          }}
                        >
                          <SelectTrigger
                            id="campaign-type"
                            className={cn(
                              "h-11 rounded-xl bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800",
                              errors.campaignType && "border-red-500 focus-visible:ring-red-500"
                            )}
                          >
                            <SelectValue placeholder="Categorize transmission" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800 z-[80]">
                            <SelectGroup>
                              <SelectLabel className="px-2 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Channel</SelectLabel>
                              <SelectItem value="whatsapp">WhatsApp Business</SelectItem>
                              <SelectItem value="message">SMS Message</SelectItem>
                              <SelectItem value="email">Email Campaign</SelectItem>
                            </SelectGroup>
                            {["email", "promotional", "informational", "follow-up", "reminder", "announcement"].includes(campaignType) && (
                              <>
                                <SelectSeparator className="my-1 ring-slate-100 dark:ring-slate-800" />
                                <SelectGroup>
                                  <SelectLabel className="px-2 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Email Specific Categories</SelectLabel>
                                  <SelectItem value="promotional">Promotional Blast</SelectItem>
                                  <SelectItem value="informational">Informational Sequence</SelectItem>
                                  <SelectItem value="follow-up">Follow-up Workflow</SelectItem>
                                  <SelectItem value="reminder">Reminder Alerts</SelectItem>
                                  <SelectItem value="announcement">Global Announcement</SelectItem>
                                </SelectGroup>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                        {errors.campaignType && <p className="text-xs text-red-500 font-medium">{errors.campaignType}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Section: Sender Information */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 text-slate-900 dark:text-white mb-2">
                      <UserCircle2 className="h-5 w-5 text-indigo-500" />
                      <h3 className="font-bold text-lg">Authority Details</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 ring-1 ring-slate-100 dark:ring-slate-800 p-6 rounded-2xl bg-white dark:bg-[#0f172a]">
                      <div className="space-y-2">
                        <Label className="text-xs text-slate-500 uppercase tracking-widest font-bold">{authDetails.label1}</Label>
                        <div className="h-11 px-4 flex items-center bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-sm text-slate-700 dark:text-slate-300 font-medium truncate">
                          {authDetails.value1}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-slate-500 uppercase tracking-widest font-bold">{authDetails.label2}</Label>
                        <div className="h-11 px-4 flex items-center bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-sm text-slate-700 dark:text-slate-300 font-medium">
                          {authDetails.value2}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold flex items-center gap-1">Target Segment <span className="text-red-500">*</span></Label>
                        <Select value={sendTo} onValueChange={setSendTo}>
                          <SelectTrigger className="h-11 rounded-xl bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
                            <SelectValue placeholder="Status Filter" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800 z-[80]">
                            <SelectItem value="new-enquiries">New Enquiries</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="application-submitted">Application Submitted</SelectItem>
                            <SelectItem value="admission-offered">Admission Offered</SelectItem>
                            <SelectItem value="enrolled">Enrolled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Section: Message Content */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between gap-2 text-slate-900 dark:text-white mb-2">
                      <div className="flex items-center gap-2">
                        {campaignType === "whatsapp" ? (
                          <MessageSquare className="h-5 w-5 text-green-500" />
                        ) : campaignType === "message" ? (
                          <Send className="h-5 w-5 text-blue-500" />
                        ) : (
                          <FileText className="h-5 w-5 text-emerald-500" />
                        )}
                        <h3 className="font-bold text-lg">
                          {campaignType === "whatsapp" ? "WhatsApp Assets" : campaignType === "message" ? "SMS Assets" : "Communication Assets"}
                        </h3>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1 text-xs text-slate-500 cursor-help bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                              <Info className="h-3 w-3" /> Syntax Guide
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="w-80 p-4 border-slate-200 dark:border-slate-700 z-[80]">
                            <p className="font-bold text-sm mb-2">Dynamic Variables</p>
                            <ul className="space-y-1 text-xs">
                              <li><code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{{ud.name}}"}</code> - Recipient Name</li>
                              <li><code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{{ud.email}}"}</code> - Recipient Email</li>
                              <li><code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"**text**"}</code> - Bold (Markdown)</li>
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <div className="space-y-6 ring-1 ring-slate-100 dark:ring-slate-800 p-8 rounded-3xl bg-white dark:bg-[#0f172a] backdrop-blur-sm">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                        {campaignType === "email" && (
                          <div className="md:col-span-8 space-y-2">
                            <Label htmlFor="subject" className="text-sm font-semibold">Broadcast Subject <span className="text-red-500">*</span></Label>
                            <Input
                              id="subject"
                              placeholder="Premium Headline..."
                              value={subject}
                              onChange={(e) => {
                                setSubject(e.target.value);
                                setErrors({ ...errors, subject: "" });
                              }}
                              className={cn(
                                "h-12 rounded-xl bg-white dark:bg-[#020617] border-slate-200 dark:border-slate-800 font-medium",
                                errors.subject && "border-red-500 focus-visible:ring-red-500"
                              )}
                            />
                            {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
                          </div>
                        )}

                        <div className={cn(campaignType === "email" ? "md:col-span-4" : "md:col-span-12", "space-y-2")}>
                          <Label htmlFor="template" className="text-sm font-semibold flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5 text-amber-500" /> {campaignType === "email" ? "Smart Templates" : `${campaignType.charAt(0).toUpperCase() + campaignType.slice(1)} Templates`}
                          </Label>
                          <Select
                            value={emailTemplate}
                            onValueChange={(value) => {
                              if (value === "add-custom") setIsAddCustomOpen(true);
                              else setEmailTemplate(value);
                            }}
                          >
                            <SelectTrigger className="h-12 rounded-xl bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
                              <SelectValue placeholder="Choose a style" />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800 z-[80]">
                              {templates
                                .filter(t => t.type === (campaignType === "whatsapp" || campaignType === "message" ? campaignType : "email"))
                                .map((t) => (
                                  <div key={t.id} className="flex justify-between items-center pr-2">
                                    <SelectItem value={t.id}>{t.name}</SelectItem>
                                    {t.is_custom && (
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id); }}
                                        className="text-red-500 hover:text-red-600 hover:bg-red-500/5 h-7 w-7 rounded-lg"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              {templates.filter((t) => t.is_custom).length < 2 && (
                                <>
                                  <Separator className="my-1 ring-slate-100 dark:ring-slate-800" />
                                  <SelectItem value="add-custom" className="text-blue-600 focus:text-blue-700 font-bold">
                                    <span className="flex items-center gap-2">
                                      <PlusCircle className="h-4 w-4" /> Create Custom Template
                                    </span>
                                  </SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email-body" className="text-sm font-semibold">Message Body <span className="text-red-500">*</span></Label>
                        <div className="relative border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/20 transition-all shadow-inner">
                          <div className="bg-slate-100/50 dark:bg-slate-900/50 px-4 py-2 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-600" />
                              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{campaignType === "email" ? "Body Editor" : "Message Editor"}</span>
                            </div>
                          </div>
                          <Textarea
                            id="email-body"
                            placeholder="Compose your message..."
                            value={emailBody}
                            onChange={(e) => {
                              setEmailBody(e.target.value);
                              setErrors({ ...errors, emailBody: "" });
                            }}
                            className={cn(
                              "min-h-[200px] border-0 rounded-none focus-visible:ring-0 bg-white dark:bg-[#020617] text-slate-800 dark:text-slate-200 p-6 leading-relaxed text-base resize-none",
                              errors.emailBody && "ring-1 ring-red-500"
                            )}
                          />
                        </div>
                        {errors.emailBody && <p className="text-xs text-red-500 mt-1">{errors.emailBody}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Section: Recipient Review */}
                  <div className="space-y-6 pb-10">
                    <div className="flex items-center gap-2 text-slate-900 dark:text-white mb-2">
                      <Target className="h-5 w-5 text-amber-500" />
                      <h3 className="font-bold text-lg">Final Recipient Review</h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {selectedContacts.map((contact) => (
                        <div
                          key={contact.id}
                          className="group p-3 bg-white dark:bg-[#0f172a] border border-slate-100 dark:border-slate-800 rounded-xl hover:border-primary transition-all duration-200 flex items-center gap-3 shadow-sm"
                        >
                          <div className="h-9 w-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-primary group-hover:bg-primary/5 transition-colors">
                            <UserCircle2 className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-xs text-slate-900 dark:text-white truncate">{contact.name}</p>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{contact.email}</p>
                          </div>
                          <ChevronRight className="h-3 w-3 text-slate-300 group-hover:text-primary transition-colors" />
                        </div>
                      ))}
                      {selectedContacts.length === 0 && (
                        <div className="col-span-full py-10 text-center bg-slate-50 dark:bg-[#020617] rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                          <p className="text-slate-400 font-medium text-sm">No contacts selected to review</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>

            {/* Action Footer */}
            <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-[#020617] flex items-center justify-between shrink-0">
              <Button
                variant="ghost"
                onClick={handleClose}
                className="rounded-xl px-6 h-12 text-slate-600 dark:text-slate-400 font-bold hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Abort Campaign
              </Button>
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleSendNow}
                  disabled={selectedContacts.length === 0 || isSending}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl px-10 h-12 font-bold shadow-lg shadow-blue-500/25 flex items-center gap-3 transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSending ? (
                    <>
                      <Sparkles className="h-5 w-5 animate-pulse" />
                      <span>Transmitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      <span>Execute Campaign</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>

          {isAddCustomOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-md z-[100] animate-in fade-in zoom-in duration-200">
              <div className="bg-white dark:bg-[#0f172a] p-8 rounded-3xl w-full max-w-[500px] space-y-8 border border-slate-200 dark:border-slate-800 m-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">New Custom Template</h3>
                </div>

                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Template Logic Name</Label>
                    <Input placeholder="Marketing Special..." value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)} className="h-12 rounded-xl bg-slate-50 dark:bg-[#020617] border-slate-200 dark:border-slate-800" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Subject Blueprint</Label>
                    <Input placeholder="Blueprint for subject..." value={newTemplateSubject} onChange={e => setNewTemplateSubject(e.target.value)} className="h-12 rounded-xl bg-slate-50 dark:bg-[#020617] border-slate-200 dark:border-slate-800" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Payload Content</Label>
                    <Textarea placeholder="Core message payload..." value={newTemplateBody} onChange={e => setNewTemplateBody(e.target.value)} className="min-h-[150px] rounded-xl bg-slate-50 dark:bg-[#020617] border-slate-200 dark:border-slate-800 p-4" />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="ghost" onClick={() => setIsAddCustomOpen(false)} className="rounded-xl px-6 h-12 font-bold">Discard</Button>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-12 font-bold shadow-lg shadow-blue-500/20"
                    onClick={() => {
                      if (!newTemplateName || !newTemplateSubject || !newTemplateBody) return toast.error("All parameters required");
                      const newId = "custom-" + Date.now();
                      const templateType = (campaignType === "whatsapp" || campaignType === "message") ? campaignType : "email";
                      setTemplates([...templates, {
                        id: newId,
                        name: newTemplateName,
                        subject: newTemplateSubject,
                        body: newTemplateBody,
                        is_custom: true,
                        type: templateType
                      }]);
                      setEmailTemplate(newId);
                      setNewTemplateName(""); setNewTemplateSubject(""); setNewTemplateBody("");
                      setIsAddCustomOpen(false);
                    }}
                  >
                    Integrate Template
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
